
import { SystemConfig, Driver } from '../../../types';
import { getFleetPerformanceIndex } from '../fleet/telemetry';

export interface TimeBreakdown {
    outbound: number;
    inter: number;
    return: number;
    service: number;
    totalReal: number;
}

export interface InternalCostResult {
    cashCost: number;
    opportunityCost: number;
    totalDecisionCost: number;
    costPerMinute: number;
    breakdown: TimeBreakdown;
    isHeuristic: boolean;
    auditLogs: string[];
}

/**
 * MOTOR DE CUSTO v4.6 (Fixed Asset Logic)
 * Removido completamente o Pulso/Oráculo como fator de decisão.
 * O custo reflete o valor nominal do recurso (tempo) + taxas.
 */
export const calculateInternalCostV2 = (
    timeMetrics: { outbound: number; inter: number; return: number },
    isHeuristicData: boolean,
    ordersCount: number,
    loadFactor: number, // Mantido apenas por compatibilidade de assinatura
    config: SystemConfig,
    isDriverIdle: boolean,
    driverSpeedFactor: number = 1.0,
    allDrivers: Driver[] = []
): InternalCostResult => {
    const logs: string[] = [];

    // 1. Constantes de Base (Custo Real do Recurso)
    const dailyFixed = config.dailyFixedCost;
    const shiftMinutes = (config.averageShiftHours || 8) * 60;
    const costPerMinute = parseFloat((dailyFixed / shiftMinutes).toFixed(4));
    
    // 2. Cálculos de Tempo com Fator de Velocidade
    const speedMultiplier = 1 / Math.max(0.5, driverSpeedFactor);
    const adjustedOutbound = timeMetrics.outbound * speedMultiplier;
    const adjustedInter = timeMetrics.inter * speedMultiplier;
    const adjustedReturn = timeMetrics.return * speedMultiplier;
    
    const totalServiceTime = ordersCount * (config.fleetConfig.serviceTimeMinutes || 3);
    const totalTravelTime = adjustedOutbound + adjustedInter + adjustedReturn;
    const totalRealTime = totalTravelTime + totalServiceTime;

    // 3. Cash Cost (Gasto financeiro direto)
    const timeCost = totalRealTime * costPerMinute;
    const feesCost = config.driverFixedFeePerOrder * ordersCount;
    const cashCost = parseFloat((timeCost + feesCost).toFixed(2));

    // 4. Multiplicador de Decisão (Estratégia)
    // v4.6: strategyMultiplier é neutro (1.0), independente do pulso do hub.
    let strategyMultiplier = 1.0; 

    // Bonus de Ociosidade: Recompensa a eficiência de despachar quem já está parado no Hub.
    if (isDriverIdle) {
        strategyMultiplier *= 0.5;
        logs.push("Driver Idle: Incentivo de disponibilidade imediata.");
    }

    const totalDecisionCostRaw = cashCost * strategyMultiplier;
    const opportunityCost = parseFloat((totalDecisionCostRaw - cashCost).toFixed(2));
    const totalDecisionCost = parseFloat(totalDecisionCostRaw.toFixed(2));

    if (isHeuristicData) {
        logs.push("⚠️ DATA_SOURCE_HEURISTIC (Google Offline)");
    }

    logs.push(`Ocupação Bag: ${ordersCount}/${config.fleetConfig.maxOrdersPerRoute || 4}`);

    return {
        cashCost,
        opportunityCost,
        totalDecisionCost,
        costPerMinute,
        breakdown: {
            outbound: parseFloat(adjustedOutbound.toFixed(1)),
            inter: parseFloat(adjustedInter.toFixed(1)),
            return: parseFloat(adjustedReturn.toFixed(1)),
            service: totalServiceTime,
            totalReal: parseFloat(totalRealTime.toFixed(1))
        },
        isHeuristic: isHeuristicData,
        auditLogs: logs
    };
};
